<template>
  <div class="rule-box">
    <div class="mb-5 card">
      <loading
        loader="dots"
        :active.sync="loading"
        :is-full-page="false"
        color="#2196f3"
      ></loading>
      <div
        class="d-flex justify-content-between align-items-center card-header"
      >
        <div class="d-flex flex-column title">
          <h2 v-if="!form.editable">{{ form.title }}</h2>
          <div class="d-flex flex-column" v-else>
            <span class="">Title</span>
            <b-form-input
              type="text"
              v-model="form.title"
              placeholder="Enter rule title"
            />
          </div>
          <span v-if="errors.title" class="text-danger">
            {{ errors.title }}
          </span>
        </div>

        <div class="d-flex justify-content-end align-items-center toolbar">
          <b-badge class="mx-2" :variant="!form.status ? 'warning' : 'info'">
            {{ !form.status ? "Disable" : "Enable" }}
          </b-badge>
          <b-button
            variant="success"
            @click="onDuplicate"
            v-if="currentUser.role === 'SuperAdmin'"
          >
            Duplicate
          </b-button>
          <b-button class="bg-white ml-4" @click="toggleVisibility">
            <i v-if="form.visible" class="text-dark fa fa-chevron-up" />
            <i v-else class="text-dark fa fa-chevron-down" />
          </b-button>
          <b-button
            class="bg-white ml-4 handleBlocks"
            v-show="reOrderMode && !form.new"
            @click.prevent=""
          >
            <i class="text-dark fa fa-align-justify"></i>
          </b-button>
        </div>
      </div>
      <b-collapse v-model="form.visible">
        <div class="card-body">
          <div
            class="d-flex justify-content-end tools"
            v-if="currentUser.role === 'SuperAdmin'"
          >
            <b-button variant="danger" class="mr-4" @click="deleteBlock">
              Delete
            </b-button>
            <b-button
              v-if="!form.new"
              :variant="form.status ? 'warning' : 'info'"
              class="mr-4"
              @click="toggleStatus"
            >
              <span>{{ form.status ? "Disable" : "Enable" }}</span>
            </b-button>
            <b-button
              :variant="form.editable ? 'outline-warning' : 'outline-primary'"
              @click="toggleEditability"
            >
              <i v-if="form.editable" class="fa fa-times" />
              <i v-else class="fa fa-pen" />
            </b-button>
          </div>
          <div
            class="d-flex flex-column card-content"
            :class="{ disabled: !form.editable }"
          >
            <!-- match -->
            <div class="d-flex flex-column match">
              <h3 class="text-primary">Match:</h3>
              <draggable
                v-model="form.match"
                handle=".match-handle"
                :disabled="form.match.length <= 1"
              >
                <transition-group>
                  <div
                    class="d-flex align-items-center"
                    v-for="(match, idx) in form.match"
                    :key="match.id"
                  >
                    <div class="d-flex align-self-center">
                      <i class="match-handle fa fa-align-justify"></i>
                    </div>
                    <div class="col-4">
                      <!-- condition name -->
                      <b-form-group label="Condition Name">
                        <b-form-select
                          :options="conditionNames"
                          v-model="match.condition_name"
                        >
                          <template v-slot:first>
                            <b-form-select-option value="">
                              Select Condition Name
                            </b-form-select-option>
                          </template>
                        </b-form-select>
                        <span
                          v-if="errors[`match.${idx}.condition_name`]"
                          class="text-danger"
                        >
                          {{ errors[`match.${idx}.condition_name`] }}
                        </span>
                      </b-form-group>
                    </div>
                    <div class="col-3">
                      <!-- operator -->
                      <b-form-group label="Operator">
                        <b-form-select
                          :options="operators"
                          v-model="match.operator"
                        >
                          <template v-slot:first>
                            <b-form-select-option value="">
                              Select Operator
                            </b-form-select-option>
                          </template>
                        </b-form-select>
                        <span
                          v-if="errors[`match.${idx}.operator`]"
                          class="text-danger"
                        >
                          {{ errors[`match.${idx}.operator`] }}
                        </span>
                      </b-form-group>
                    </div>
                    <div class="col-4">
                      <b-form-group label="Value">
                        <ACInput
                          v-model="match.value"
                          :items="conditionNames"
                          :rejectKeys="[' ']"
                        />
                        <span
                          v-if="errors[`match.${idx}.value`]"
                          class="text-danger"
                        >
                          {{ errors[`match.${idx}.value`] }}
                        </span>
                      </b-form-group>
                    </div>
                    <div class="d-flex col-1">
                      <button
                        class="ml-auto p-0 btn"
                        @click.prevent="onRemoveMatch(idx)"
                      >
                        <i class="text-danger fa fa-times"></i>
                      </button>
                    </div>
                  </div>
                </transition-group>
              </draggable>
              <b-button class="ml-auto" @click.prevent="onAddMatch">
                <i class="fa fa-plus" />
                <span>add match</span>
              </b-button>
            </div>

            <!-- deploy -->
            <div class="d-flex flex-column deploy">
              <h3 class="text-primary">Deploy:</h3>
              <draggable
                v-model="form.deploy"
                handle=".deploy-handle"
                :disabled="form.deploy.length <= 1"
              >
                <transition-group>
                  <div
                    class="d-flex align-items-center"
                    v-for="(deploy, idx) in form.deploy"
                    :key="deploy.id"
                  >
                    <div class="d-flex align-self-center">
                      <i class="deploy-handle fa fa-align-justify"></i>
                    </div>

                    <div class="mx-2">
                      <b-form-group label="Variable Name">
                        <b-form-select
                          v-model="deploy.variable_name"
                          :options="variable_names"
                        >
                          <template v-slot:first>
                            <b-form-select-option value="">
                              Select Variable Name
                            </b-form-select-option>
                          </template>
                        </b-form-select>
                        <span
                          v-if="errors[`deploy.${idx}.variable_name`]"
                          class="text-danger"
                        >
                          {{ errors[`deploy.${idx}.variable_name`] }}
                        </span>
                      </b-form-group>
                    </div>

                    <b-form-group label="Variable Name Value">
                      <div class="d-flex">
                        <b-form-radio-group
                          v-model="deploy.varNameType"
                          @change="
                            () => {
                              deploy.variable_name_value.name = '';
                              deploy.variable_name_value.params = [];
                            }
                          "
                          :options="varNameTypes"
                          buttons
                        ></b-form-radio-group>
                        <b-form-select
                          v-if="deploy.varNameType == 'fun'"
                          @change="
                            () => (deploy.variable_name_value.params = [])
                          "
                          v-model="deploy.variable_name_value.name"
                          :options="functions"
                        />
                      </div>
                    </b-form-group>
                    <b-form-group class="mt-8" style="flex: 1">
                      <div
                        class="d-flex mx-2"
                        v-if="deploy.varNameType == 'fun'"
                      >
                        <b-form-input
                          v-for="(param, idx) in functionsParams[
                            deploy.variable_name_value.name
                          ]"
                          :key="param"
                          class="mx-2"
                          type="text"
                          v-model="deploy.variable_name_value.params[idx]"
                          :placeholder="`Enter Variable#${idx + 1} Name`"
                        >
                        </b-form-input>
                      </div>

                      <div class="col" v-if="deploy.varNameType == 'text'">
                        <ACInput
                          :items="variable_names"
                          v-model="deploy.variable_name_value.name"
                          placeholder="Enter Variable Name"
                        />
                        <span
                          v-if="errors[`deploy.${idx}.variable_name_value`]"
                          class="text-danger"
                        >
                          {{ errors[`deploy.${idx}.variable_name_value`] }}
                        </span>
                      </div>
                    </b-form-group>

                    <div class="d-flex col-1">
                      <button
                        class="ml-auto p-0 btn"
                        @click.prevent="onRemoveDeploy(idx)"
                      >
                        <i class="text-danger fa fa-times"></i>
                      </button>
                    </div>
                  </div>
                </transition-group>
              </draggable>
              <b-button class="ml-auto" @click.prevent="onAddDeploy">
                <i class="fa fa-plus" />
                <span>add deploy</span>
              </b-button>
            </div>

            <!-- actions -->
            <div class="d-flex justify-content-center" v-if="form.editable">
              <b-button
                v-if="form.new"
                variant="primary"
                @click.prevent="addBlock"
              >
                add
              </b-button>

              <b-button v-else variant="primary" @click.prevent="updateBlock">
                update
              </b-button>
            </div>
          </div>
        </div>
      </b-collapse>
    </div>
  </div>
</template>

<script>
import http from "@/axios";
import draggable from "vuedraggable";
import Vue from "vue";
import ACInput from "./ACInput.vue";
import { parseVariables, stitchVariables } from "@/utils";
import { mapGetters } from "vuex";
Vue.component("draggable", draggable);
export default {
  components: { ACInput },
  computed: {
    ...mapGetters(["currentUser"]),
  },
  props: {
    title: String,
    match: Array,
    deploy: Array,
    id: String,
    idx: Number,
    parent_id: String,
    position: Number,
    status: Number,
    conditionNames: Array,
    variable_names: Array,
    editable: Boolean,
    visible: Boolean,
    new: Boolean,
    reOrderMode: Boolean,
    base: String,
  },
  data() {
    return {
      errors: {},
      loading: false,
      form: {
        new: this.new,
        editable: this.editable,
        visible: this.visible,
        title: this.title,
        match: this.match,
        deploy: this.deploy,
        id: this.id,
        parent_id: this.parent_id,
        position: this.position,
        status: this.status,
      },
      operators: [
        "equal",
        "!equal",
        "startwith",
        "!startwith",
        "existin_url",
        "!existin_url",
        "between",
        "!between",
      ],
      functionsParams: {
        func_strip: 1,
        func_do_strip_append: 2,
        func_append: 1,
        url: 1,
      },
      functions: [
        {
          text: "please select a function",
          value: "",
        },
        {
          text: "func_strip",
          value: "func_strip",
        },
        {
          text: "func_do_strip_append",
          value: "func_do_strip_append",
        },
        {
          text: "func_append",
          value: "func_append",
        },
        { name: "url", text: "url", value: "url", params: 1 },
      ],
      varNameTypes: [
        { text: "Function", value: "fun" },
        { text: "Text", value: "text" },
      ],
    };
  },
  methods: {
    // utils
    toggleVisibility() {
      this.form.visible = !this.form.visible;
    },
    toggleEditability() {
      this.form.editable = !this.form.editable;
    },
    onRemoveMatch(idx) {
      this.form.match.splice(idx, 1);
    },
    onAddMatch() {
      this.form.match.push({
        id: new Date().getTime(),
        condition_name: "",
        operator: "",
        value: "",
        related_variables: [],
      });
    },
    onRemoveDeploy(idx) {
      this.form.deploy.splice(idx, 1);
    },
    onAddDeploy() {
      this.form.deploy.push({
        id: new Date().getTime(),
        variable_name: "",
        variable_name_value: {
          name: "",
          params: [],
        },
        varNameType: "fun",
        related_variables: [],
      });
    },

    // emits
    refreshBlocks() {
      this.$emit("refreshBlocks");
    },
    onDuplicate() {
      this.$emit("duplicate");
    },
    deleteBlock() {
      this.$emit("delete");
    },

    // api requests
    async addBlock() {
      let { match, deploy } = this;
      const { form, parent_id } = this;
      this.loading = true;

      match = match.map((matchRule) => ({
        ...matchRule,
        related_variables: parseVariables(matchRule.value),
      }));
      deploy = deploy.map((deployRule) => ({
        ...deployRule,
        variable_name_value: stitchVariables(deployRule.variable_name_value),
        related_variables: parseVariables(deployRule.variable_name_value.name),
      }));

      const block = {
        title: form.title,
        match,
        deploy,
        position: this.idx,
      };

      try {
        const res = await http.post(`${this.base}/rules/${parent_id}`, block);
        this.refreshBlocks();
        this.loading = false;
        this.form.editable = false;
        this.form.new = false;
        this.form.status = true;
        this.$notify({
          group: "add-rule",
          title: res.data.message,
          type: "success",
        });
      } catch (error) {
        this.errors = error.response.data.error;
        const errorMessage = error.response
          ? error.response.data.message
          : error.message;

        this.$notify({
          group: "edit-rule",
          title: errorMessage,
          type: "error",
        });
        this.loading = false;
      }
    },
    async updateBlock() {
      let { match, deploy } = this;
      const { form, position, status, parent_id, id } = this;
      this.loading = true;

      match = match.map((matchRule) => ({
        ...matchRule,
        related_variables: parseVariables(matchRule.value),
      }));
      deploy = deploy.map((deployRule) => ({
        ...deployRule,
        variable_name_value: stitchVariables(deployRule.variable_name_value),
        related_variables: parseVariables(deployRule.variable_name_value.name),
      }));

      const block = {
        title: form.title,
        match,
        deploy,
        position,
        status,
        parent_id,
        id,
      };

      try {
        const res = await http.put(`${this.base}/rules/${id}`, block);
        this.loading = false;
        this.form.editable = false;
        this.errors = {};
        this.$notify({
          group: "edit-rule",
          title: res.data.message,
          type: "success",
        });
      } catch (error) {
        this.errors = error.response.data.error;
        const errorMessage = error.response
          ? error.response.data.message
          : error.message;
        this.$notify({
          group: "edit-rule",
          title: errorMessage,
          type: "error",
        });
        this.loading = false;
      }
    },
    async toggleStatus() {
      this.loading = true;
      try {
        const res = await http.put(
          `${this.base}/rules/update-status/${this.id}`,
          {
            status: +!this.form.status,
          }
        );

        const status = +!this.form.status;
        this.form.status = status;
        this.$emit("update:status", status);

        this.$notify({
          group: "edit-rule",
          title: res.data.message,
          type: "success",
        });
        this.loading = false;
      } catch (error) {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.title {
  flex: 1;
}
.toolbar {
  flex: 1;
}
</style>

<style lang="scss">
.handle,
.handleBlocks {
  cursor: move;
}
.card-content.disabled {
  pointer-events: none;
  opacity: 0.7;
}
.flaticon-edit,
.flaticon-close {
  font-weight: bolder;
  font-size: 20px !important;
  color: #2196f3 !important;
}
.varNameTypes {
  .input-group-text {
    padding: 0 !important;
    background-color: #fff !important;
  }
  .custom-select,
  input[type="text"] {
    height: 40px;
  }
  .btn.btn-outline-primary {
    color: #111;
    background-color: transparent;
    border-color: #f3f6f9;
    border-radius: 0;
  }

  .btn.btn-outline-primary:not(:disabled):not(.disabled):active:not(.btn-text),
  .btn.btn-outline-primary:not(:disabled):not(.disabled).active,
  .show > .btn.btn-outline-primary.dropdown-toggle,
  .show .btn.btn-outline-primary.btn-dropdown {
    background-color: #f3f6f9;
    border-color: #f3f6f9;
    color: #111;
  }
}
</style>
